/**
 * useDesktopIncomingCall — Electron-only bridge between the existing
 * `IncomingCallModal` subscription (which is the single source of truth
 * for incoming-call events) and the native macOS overlay owned by the
 * Electron main process.
 *
 * Этот хук НЕ открывает собственную subscription. Он:
 *   1. Слушает события `incoming-call:answered` / `incoming-call:declined`
 *      от Electron main (пользователь нажал кнопку в нативном окне /
 *      tray-меню / системном Notification).
 *   2. Проксирует их в `IncomingCallModal` через глобальный emitter —
 *      модалка подписана и вызывает уже существующие handlers
 *      `CallsProvider.accept` / `CallsProvider.end`.
 *
 * Сам показ нативного окна происходит из IncomingCallModal (он видит
 * входящий звонок и знает, в фокусе ли main window).
 *
 * На web и mobile — no-op.
 */

import { useEffect } from "react";
import { isDesktop, incomingCall } from "../desktop";
import emitter from "lib/emitter";

/** Emitter events used to relay native button presses into IncomingCallModal. */
export const DESKTOP_CALL_EVENTS = {
  NATIVE_ANSWER: "desktop:incomingCallAnswer",
  NATIVE_DECLINE: "desktop:incomingCallDecline",
} as const;

export default function useDesktopIncomingCall(): void {
  useEffect(() => {
    if (!isDesktop) return;

    const offAnswered = incomingCall.onAnswered((payload: { callId: string }) => {
      emitter.emit(DESKTOP_CALL_EVENTS.NATIVE_ANSWER, payload);
    });
    const offDeclined = incomingCall.onDeclined((payload: { callId: string }) => {
      emitter.emit(DESKTOP_CALL_EVENTS.NATIVE_DECLINE, payload);
    });

    return () => {
      try { offAnswered?.(); } catch { /* ignore */ }
      try { offDeclined?.(); } catch { /* ignore */ }
    };
  }, []);
}
